import type { AgentFile } from "../types/api";
import { toString } from "mdast-util-to-string";
import remarkGfm from "remark-gfm";
import remarkParse from "remark-parse";
import { unified } from "unified";
import { visit } from "unist-util-visit";

export type FilePreviewKind = "markdown" | "image" | "video" | "html" | "pdf" | "unsupported";

export interface MarkdownHeading {
  depth: number;
  id: string;
  line: number;
  text: string;
}

export const FILE_PREVIEW_LIMITS: Record<Exclude<FilePreviewKind, "unsupported">, number> = {
  markdown: 2 * 1024 * 1024,
  // HTML renders in the isolated preview frame, not our DOM; reports with bundled chart libraries
  // (plotly/bokeh embed ~3–5 MB of JS) must still preview.
  html: 20 * 1024 * 1024,
  image: 20 * 1024 * 1024,
  video: 256 * 1024 * 1024,
  pdf: 30 * 1024 * 1024
};

const EXTENSIONS: Record<Exclude<FilePreviewKind, "unsupported">, Set<string>> = {
  markdown: new Set(["md", "markdown", "mdown", "mkd", "txt"]),
  html: new Set(["html", "htm"]),
  image: new Set(["png", "jpg", "jpeg", "gif", "webp", "svg", "ico", "bmp", "avif"]),
  video: new Set(["mp4", "webm", "ogv", "ogg", "mov", "m4v"]),
  pdf: new Set(["pdf"])
};

const VIDEO_MIME: Record<string, string> = {
  mp4: "video/mp4",
  webm: "video/webm",
  ogv: "video/ogg",
  ogg: "video/ogg",
  mov: "video/quicktime",
  m4v: "video/x-m4v"
};

export function fileExtension(file: Pick<AgentFile, "name" | "path">): string {
  const name = `${file.name || file.path}`.toLowerCase();
  const index = name.lastIndexOf(".");
  return index === -1 ? "" : name.slice(index + 1);
}

export function filePreviewKind(file: Pick<AgentFile, "name" | "path">): FilePreviewKind {
  const extension = fileExtension(file);
  for (const kind of ["markdown", "html", "image", "video", "pdf"] as const) {
    if (EXTENSIONS[kind].has(extension)) return kind;
  }
  return "unsupported";
}

/**
 * Split generated files into what the user asked for, reference material the agent downloaded (folded) and
 * helper files (folded).
 */
export function splitFilesByRole<T extends Pick<AgentFile, "role">>(
  files: T[]
): { deliverables: T[]; references: T[]; process: T[] } {
  const deliverables: T[] = [];
  const references: T[] = [];
  const process: T[] = [];
  for (const file of files) {
    (file.role === "process" ? process : file.role === "reference" ? references : deliverables).push(file);
  }
  return { deliverables, references, process };
}

// Format labels sent by the backend (FORMAT_LABEL) → extensions that satisfy them.
const FORMAT_EXTENSIONS: Record<string, string[]> = {
  PPT: ["ppt", "pptx"], PDF: ["pdf"], Word: ["doc", "docx"], Excel: ["xls", "xlsx"], HTML: ["html", "htm"]
};

/** Requested formats still not delivered: a later (e.g. recovered) deliverable of that format clears the note. */
export function stillPendingFormats(pending: string[], files: Array<Pick<AgentFile, "name" | "path" | "role">>): string[] {
  const delivered = new Set(files.filter((file) => file.role !== "process" && file.role !== "reference").map(fileExtension));
  return pending.filter((label) => !(FORMAT_EXTENSIONS[label] ?? []).some((ext) => delivered.has(ext)));
}

export function linkedAgentFileFromHref(href: string): AgentFile | null {
  const withoutFragment = String(href || "").trim().split(/[?#]/, 1)[0];
  if (!withoutFragment || withoutFragment.startsWith("//") || /^[a-z][a-z\d+.-]*:/i.test(withoutFragment)) return null;

  let decoded: string;
  try {
    decoded = decodeURIComponent(withoutFragment).replaceAll("\\", "/");
  } catch {
    return null;
  }
  if (decoded.startsWith("//") || /^[a-z][a-z\d+.-]*:/i.test(decoded)) return null;
  // Codex commonly emits editor-style targets ending in `:line` or
  // `:line:column`. Those suffixes are locations, not part of the filename.
  decoded = decoded.replace(/:\d+(?::\d+)?$/, "");

  const workspacePrefix = "/opt/data/workspace/";
  const runtimePrefix = "/opt/data/";
  let path = decoded.startsWith(workspacePrefix)
    ? decoded.slice(workspacePrefix.length)
    : decoded.startsWith(runtimePrefix)
      ? decoded.slice(runtimePrefix.length)
      : decoded.replace(/^\.\//, "");
  if (!path || path.startsWith("/")) return null;

  const parts = path.split("/");
  if (parts.some((part) => !part || part === "." || part === ".." || part.startsWith("."))) return null;
  path = parts.join("/");
  const name = parts.at(-1)!;
  return { path, name, bytes: 0, updatedAt: "" };
}

export function resolveLinkedAgentFile(
  linkedFile: AgentFile,
  replyFiles: AgentFile[]
): AgentFile | null {
  return replyFiles.find((file) => file.path === linkedFile.path) ?? null;
}

export function filePreviewLimit(kind: FilePreviewKind): number | null {
  return kind === "unsupported" ? null : FILE_PREVIEW_LIMITS[kind];
}

export function previewSizeAllowed(kind: FilePreviewKind, bytes: number): boolean {
  const limit = filePreviewLimit(kind);
  return limit === null || (Number.isFinite(bytes) && bytes >= 0 && bytes <= limit);
}

export function videoMimeType(file: Pick<AgentFile, "name" | "path">): string {
  return VIDEO_MIME[fileExtension(file)] ?? "video/mp4";
}

function headingSlug(text: string): string {
  return text
    .normalize("NFKC")
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s-]/gu, "")
    .trim()
    .replace(/[\s-]+/g, "-") || "section";
}

export function extractMarkdownHeadings(markdown: string): MarkdownHeading[] {
  const headings: MarkdownHeading[] = [];
  const usedIds = new Set<string>();
  const tree = unified().use(remarkParse).use(remarkGfm).parse(markdown);

  function uniqueId(base: string): string {
    let id = base;
    let suffix = 2;
    while (usedIds.has(id)) id = `${base}-${suffix++}`;
    usedIds.add(id);
    return id;
  }

  visit(tree, "heading", (node) => {
    const text = toString(node).trim();
    const line = node.position?.start.line;
    if (!text || !line) return;
    const base = headingSlug(text);
    headings.push({
      depth: node.depth,
      id: uniqueId(base),
      line,
      text
    });
  });

  return headings;
}

export function safeMarkdownImageSource(src: string): boolean {
  return src.startsWith("blob:") || /^data:image\/(?:png|jpe?g|gif|webp|avif);base64,/i.test(src);
}

// Agent HTML runs with scripts enabled only on this separate origin (web/html-preview, served by
// scripts/serve-html-preview.ts), never on the main site. Selected by build mode like the backend URL.
export function htmlPreviewOriginForNodeEnv(nodeEnv = process.env.NODE_ENV): string {
  return nodeEnv === "production" ? "https://preview.s-j.ai" : "http://127.0.0.1:47833";
}

export const HTML_PREVIEW_ORIGIN = htmlPreviewOriginForNodeEnv();

/** The live preview is only safe on a different origin; anything else must fall back to the static render. */
export function isIsolatedPreviewOrigin(previewOrigin: string, pageOrigin: string): boolean {
  try {
    return new URL(previewOrigin).origin === previewOrigin && previewOrigin !== pageOrigin;
  } catch {
    return false;
  }
}

export function buildSandboxedHtmlDocument(sanitizedBody: string): string {
  return `<!doctype html>
<html>
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<meta http-equiv="Content-Security-Policy" content="default-src 'none'; script-src 'none'; connect-src 'none'; frame-src 'none'; object-src 'none'; form-action 'none'; base-uri 'none'; img-src data: blob:; media-src data: blob:; font-src data:; style-src 'unsafe-inline'">
<style>
html { color-scheme: light; background: #fff; }
body { box-sizing: border-box; max-width: 1100px; min-height: 100vh; margin: 0 auto; padding: 24px; color: #171717; background: #fff; font: 16px/1.55 system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif; overflow-wrap: anywhere; }
img, video, canvas, svg { max-width: 100%; height: auto; }
pre { max-width: 100%; overflow: auto; }
table { display: block; max-width: 100%; overflow: auto; border-collapse: collapse; }
</style>
</head>
<body>${sanitizedBody}</body>
</html>`;
}
